import { useCallback, useState, useEffect, type ChangeEvent, type ComponentProps, type FocusEvent } from "react";
import moment from "moment-timezone";
import { Input } from "../../../ui/input";

type DurationBlurEvent = { target: HTMLInputElement };

const renderDurationAsHoursAndMinutes = (totalSeconds: number) => {
	const totalMinutes = Math.round(totalSeconds / 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export const getDurationInputValue = (value: string) => {
	let duration = 0;
	const [hours, minutes] = value.split(":");
	if (hours && minutes) {
		duration += Number(minutes) * 60;
		duration += Number(hours) * 60 * 60;
	}
	return duration;
};

type DurationInputProps = Omit<ComponentProps<typeof Input>, "value" | "onChange"> & {
	value?: string;
	onChange?: (value: string) => void;
};

function DurationInput({ value, onChange, onBlur: onBlurProp, className, ...props }: DurationInputProps) {
	const [time, setTime] = useState("");
	const onBlur = useCallback(
		(e: DurationBlurEvent) => {
			let val = e.target.value;
			val = val.replace(/,/g, ".");

			if (isNaN(Number(val))) {
				val = val.replace(/([^0-9:]+)/, "");
				val = val.replace(/^(0|\:)/, "");

				const match = /(\d{0,7})[^:]*((?:\:\d{0,2})?)/.exec(val);

				let hours = match?.[1] ?? "";
				let minutes = match?.[2] ?? "";

				if (
					!minutes.includes(":") &&
					hours.length > 0 &&
					hours.length < 8 &&
					hours !== ""
				) {
					minutes = ":00";
				}

				if (minutes === ":00" && hours.length >= 7) {
					minutes = ":" + e.target.value.substr(e.target.value.length);
				}
				onChange?.(hours + minutes);
				setTime(hours + minutes);
			} else {
				const milliseconds = moment
					.duration(parseFloat(val), "hour")
					.asSeconds();
				val = !isNaN(Number(milliseconds)) ? renderDurationAsHoursAndMinutes(milliseconds) : "";
				onChange?.(val);
				setTime(val);
			}

			if (!value) {
				setTimeout(() => {
					e.target.setSelectionRange(1, 1);
				}, 0);
			}

			onBlurProp?.(e as unknown as FocusEvent<HTMLInputElement>);
		},
		[value, onChange, onBlurProp],
	);

	const onInputChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => setTime(e.target.value),
		[],
	);

	useEffect(() => {
		setTime(value ? value : "");
	}, [value]);

	return (
		<Input
			placeholder="00:00"
			value={time}
			onBlur={onBlur}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					onBlur(e as unknown as DurationBlurEvent);
				}
			}}
			onChange={onInputChange}
			onFocus={(e) => {
				e.target.select();
			}}
			className={className}
			{...props}
		/>
	);
}

export default DurationInput;
